import React from 'react';
import { Shield, AlertTriangle, Scale, FileText, Globe, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsOfServicePage = () => {
  const restrictedCountries = [
    'Canada',
    'China', 
    'Romania',
    'Singapore',
    'United States',
    'Countries and territories listed on the FATF and EU/UN sanctions lists'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <Scale className="w-12 h-12 text-white" />
            <h1 className="text-5xl font-bold text-white">
              Terms of FXC Services
            </h1>
          </div>
          <p className="text-xl text-white/90 max-w-4xl">
            Legal terms and conditions for FXC Management Services
          </p>
          <p className="text-sm text-white/80 mt-4">
            Last Updated: November 2024
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* Company Information */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4">
              <FileText className="w-8 h-8 text-[#0d6571] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">About FXC Management Services</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms of Service govern your use of services provided by <strong>FXC Management Services</strong>, 
                  operating under <strong>FXC Management Services B.V.</strong> within the FXC Group.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  By accessing or using our services, you agree to be bound by these terms and conditions. 
                  If you do not agree with any part of these terms, please do not use our services.
                </p>
              </div>
            </div>
          </div>

          {/* Legal and Compliance Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <Shield className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Legal and Compliance</h2>
                <p className="text-sm text-gray-600">Important regulatory and compliance information</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Nature of Services */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Nature of Our Services</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>FXC Management Services</strong>, operating under <strong>FXC Management Services B.V.</strong> within 
                  the FXC Group, provides <strong>information and consulting services only</strong>. We are <strong>not 
                  licensed</strong> as a financial advisor, asset manager, or broker, and we do not provide personalized 
                  investment advice, portfolio management, or recommendations tailored to your individual financial situation.
                </p>
              </div>

              {/* Informational Purpose */}
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">For Informational Purposes Only</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All content we share – including market summaries, trend analyses, VPS-related information, and any 
                  other materials – is for <strong>general informational and educational purposes only</strong>. 
                  It should <strong>not</strong> be interpreted as:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">A personal recommendation to buy, sell, or hold any financial instrument</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">An offer or solicitation to engage in any investment or trading activity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">A guarantee of performance, returns, or risk outcomes</span>
                  </li>
                </ul>
              </div>

              {/* Professional Advice Required */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Before Making Any Financial Decisions</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Before making any financial or business decisions, you should <strong>always</strong>:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Consult an independent, qualified financial advisor, tax specialist, or legal professional</span>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Consider your own objectives, financial situation, risk tolerance, and needs</span>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Carefully review all relevant legal and product documentation</span>
                  </div>
                </div>
              </div>

              {/* Regulatory Compliance */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Scale className="w-6 h-6 text-blue-600" />
                  Regulatory Compliance
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We take compliance seriously. <strong>FXC Management Services B.V.</strong> operates under applicable regulations 
                  within the <strong>EEC/EER</strong> and adheres to <strong>AML (Anti-Money Laundering)</strong> and 
                  <strong> KYC (Know Your Customer)</strong> requirements.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  As part of our legal obligations, we may request additional information or documentation to verify 
                  identity, source of funds, and business activities.
                </p>
              </div>

              {/* Geographic Restrictions */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Globe className="w-8 h-8 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Geographic Restrictions</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Due to regulatory and sanctions restrictions, our services are <strong>not available</strong> to 
                      residents of certain jurisdictions, including but not limited to:
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 ml-11">
                  {restrictedCountries.map((country, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-3 rounded-lg border border-red-300">
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-gray-800 font-medium">{country}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mt-4 ml-11">
                  If you are unsure whether our services are available in your country of residence, 
                  <strong> please contact us before proceeding</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Trading Risk Disclaimer */}
          <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <AlertTriangle className="w-10 h-10 text-orange-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Trading Risk Disclosure</h2>
                <p className="text-sm text-gray-600">Important information about trading risks</p>
              </div>
            </div>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">
                Trading in financial instruments, including but not limited to forex, commodities, indices, and cryptocurrencies, 
                carries a high level of risk and may not be suitable for all investors.
              </p>
              
              <div className="bg-orange-100 border-l-4 border-orange-500 p-4 rounded-r">
                <p className="font-bold text-orange-900 mb-2">Warning:</p>
                <p>
                  The high degree of leverage can work against you as well as for you. Before deciding to trade, 
                  you should carefully consider your investment objectives, level of experience, and risk appetite. 
                  <strong> There is a possibility that you may sustain a loss of some or all of your initial investment.</strong>
                </p>
              </div>

              <p>
                Past performance is not indicative of future results. Any content, tools, or information we provide 
                should not be considered as a guarantee of future performance or specific investment outcomes.
              </p>

              <p>
                You should only invest money that you can afford to lose and ensure that you fully understand the 
                risks involved. If you have any doubts, seek advice from an independent financial advisor.
              </p>
            </div>
          </div>

          {/* Liability and User Acknowledgment */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Liability and User Acknowledgment</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">By Using Our Services, You Acknowledge and Agree That:</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">
                      <strong>You are solely responsible</strong> for your own financial and business decisions
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">
                      <strong>FXC Management Services / FXC Management Services B.V.</strong> and its associates 
                      <strong> cannot be held liable</strong> for any loss or damage arising from the use of information we provide
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">
                      You will <strong>seek independent professional advice</strong> where appropriate
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">
                      You understand and accept the <strong>risks associated with trading and investing</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">
                      You have read, understood, and agree to be bound by these Terms of Service
                    </span>
                  </li>
                </ul>
              </div>

              <div className="border-t-2 border-gray-200 pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Limitation of Liability</h3>
                <p className="text-gray-700 leading-relaxed">
                  To the fullest extent permitted by law, FXC Management Services, FXC Management Services B.V., and their 
                  respective officers, directors, employees, and agents shall not be liable for any direct, indirect, 
                  incidental, special, consequential, or punitive damages, including but not limited to loss of profits, 
                  data, or other intangible losses, resulting from:
                </p>
                <ul className="mt-4 space-y-2 ml-6">
                  <li className="text-gray-700">• Your use or inability to use our services</li>
                  <li className="text-gray-700">• Any information, content, or materials provided through our services</li>
                  <li className="text-gray-700">• Any investment or trading decisions made based on information we provide</li>
                  <li className="text-gray-700">• Unauthorized access to or alteration of your data</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-[#0d6571] to-[#0d6571]/80 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About These Terms?</h2>
            <p className="text-white/90 mb-6">
              If you have any questions or concerns about these Terms of Service, please contact us.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-3 bg-white text-[#0d6571] rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              <strong>FXC Management Services</strong> operates under <strong>FXC Management Services B.V.</strong>
            </p>
            <p className="mt-2">
              Registered Address: Industrieweg 9, 2254AE Voorschoten, The Netherlands
            </p>
            <p className="mt-2">
              Chamber of Commerce Registration: 78204992
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
